import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ClipboardList, MessageSquare, DollarSign, Users, FileText, Building2, Calendar, Check, X, Link2, Paperclip, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ticketService } from '../services/ticketService';
import { expenseService } from '../services/expenseService';
import { notificationService } from '../services/notificationService';
import { providerService } from '../services/providerService';
import { chatService } from '../services/chatService';
import { bidService } from '../services/bidService';
import Sidebar from '../components/Sidebar';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { storage } from '../firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const TicketDetail = () => {
  const { id } = useParams();
  const { currentUser, userData } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [newExpense, setNewExpense] = useState({
    amount: '',
    category: '',
    description: ''
  });
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [ticketChat, setTicketChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(true);
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [assignedProviders, setAssignedProviders] = useState([]);
  const [assignedProvidersLoading, setAssignedProvidersLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bids, setBids] = useState([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [isOpenToBid, setIsOpenToBid] = useState(ticket?.isOpenToBid || false);

  useEffect(() => {
    if (id) {
      const unsubscribeTicket = ticketService.subscribeToTicket(id, (ticketData) => {
        setTicket(ticketData);
        setLoading(false);
      });

      const unsubscribeExpenses = expenseService.subscribeToExpensesByTicket(id, (expensesData) => {
        setExpenses(expensesData);
      });

      return () => {
        unsubscribeTicket();
        unsubscribeExpenses();
      };
    }
  }, [id]);

  // Initialize or get ticket chat
  useEffect(() => {
    if (ticket && currentUser) {
      const initializeTicketChat = async () => {
        try {
          // Get existing conversations for this ticket
          const existingConversations = await chatService.getConversationsByTicket(id);
          let conversationId = null;

          if (existingConversations.length > 0) {
            // Use existing conversation
            conversationId = existingConversations[0].id;
            setTicketChat(existingConversations[0]);
          } else {
            // Create new group chat for ticket
            const serviceProviderIds = ticket.serviceProviderIds || [];
            conversationId = await chatService.createTicketGroupChat(
              id,
              ticket.title,
              ticket.ownerId,
              ticket.tenantId,
              serviceProviderIds
            );
            
            // Get the created conversation
            const newConversation = await chatService.getConversation(conversationId);
            setTicketChat(newConversation);
          }

          // Subscribe to messages
          if (conversationId) {
            const unsubscribeMessages = chatService.subscribeToMessages(conversationId, (messages) => {
              setChatMessages(messages);
              setChatLoading(false);
            });

            return unsubscribeMessages;
          }
        } catch (error) {
          console.error('Error initializing ticket chat:', error);
          setChatLoading(false);
        }
      };

      initializeTicketChat();
    }
  }, [ticket, currentUser, id]);

  // Fetch assigned provider profiles when provider IDs change
  useEffect(() => {
    const fetchAssignedProviders = async () => {
      if (!ticket?.serviceProviderIds || ticket.serviceProviderIds.length === 0) {
        setAssignedProviders([]);
        return;
      }
      setAssignedProvidersLoading(true);
      try {
        const results = [];
        for (const providerId of ticket.serviceProviderIds) {
          try {
            const snap = await getDoc(doc(db, 'users', providerId));
            if (snap.exists()) {
              results.push({ id: snap.id, ...snap.data() });
            }
          } catch (e) {
            // Skip errors per provider to avoid blocking the whole list
            console.error('Failed to fetch provider user:', providerId, e);
          }
        }
        setAssignedProviders(results);
      } finally {
        setAssignedProvidersLoading(false);
      }
    };

    fetchAssignedProviders();
  }, [ticket?.serviceProviderIds]);

  // Subscribe to bids for this ticket
  useEffect(() => {
    if (id && userData?.role === 'owner') {
      setBidsLoading(true);
      console.log('Subscribing to bids for ticket:', id);
      
      const unsubscribeBids = bidService.subscribeToBidsByTicket(id, (bidsData) => {
        console.log('Received bids data:', bidsData);
        setBids(bidsData || []);
        setBidsLoading(false);
      });

      // Set a timeout to prevent infinite loading if subscription fails
      const timeoutId = setTimeout(() => {
        console.log('Bids loading timeout reached');
        setBidsLoading(false);
      }, 10000); // 10 second timeout

      return () => {
        unsubscribeBids();
        clearTimeout(timeoutId);
      };
    }
  }, [id, userData?.role]);

  // Update isOpenToBid state when ticket data changes
  useEffect(() => {
    if (ticket) {
      setIsOpenToBid(ticket.isOpenToBid || false);
    }
  }, [ticket]);

  const handleStatusUpdate = async (newStatus) => {
    try {
      await ticketService.updateTicket(id, { status: newStatus });
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  const handleOpenToBid = async () => {
    try {
      await ticketService.updateTicket(id, { isOpenToBid: true });
      setIsOpenToBid(true);
      alert('Ticket is now open to bidding! Service providers can submit proposals.');
    } catch (error) {
      console.error('Error opening ticket to bid:', error);
      alert('Error opening ticket to bid: ' + error.message);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await expenseService.createExpense({
        ticketId: id,
        propertyId: ticket.propertyId,
        amount: parseFloat(newExpense.amount),
        category: newExpense.category,
        description: newExpense.description,
        addedBy: currentUser.uid
      });
      setNewExpense({ amount: '', category: '', description: '' });
    } catch (error) {
      console.error('Error adding expense:', error);
    }
  };

  const generateReferralLink = async () => {
    try {
      const link = await providerService.generateReferralLink(currentUser.uid, userData);
      setReferralLink(link);
    } catch (error) {
      console.error('Error generating referral link:', error);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      alert('Referral link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleSendChatMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !ticketChat || sending) return;

    try {
      setSending(true);
      await chatService.sendMessage(ticketChat.id, {
        text: newMessage.trim(),
        senderId: currentUser.uid,
        senderName: userData?.displayName || currentUser.email,
        type: 'text'
      });
      // Notify other participants (non-blocking)
      try {
        const recipients = (ticketChat.participants || []).filter((id) => id !== currentUser.uid);
        await Promise.all(
          recipients.map((recipientId) =>
            notificationService.createNotification({
              recipientId,
              type: 'chat_message',
              title: ticketChat.title || ticket.title || 'New message',
              body: newMessage.trim(),
              resourceType: 'conversation',
              resourceId: ticketChat.id,
            })
          )
        );
      } catch (e) {
        console.error('Failed to send chat notifications:', e);
      }
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file || !ticketChat) return;
    setUploading(true);
    const path = `conversations/${ticketChat.id}/${Date.now()}_${file.name}`;
    const fileRef = storageRef(storage, path);
    const uploadTask = uploadBytesResumable(fileRef, file, { contentType: file.type || 'application/octet-stream' });

    uploadTask.on(
      'state_changed',
      () => {},
      (error) => {
        console.error('Upload error:', error);
        alert('Failed to upload attachment. Please try again.');
        setUploading(false);
        event.target.value = '';
      },
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const isImage = file.type.startsWith('image/');
          const textFallback = isImage ? 'Sent a photo' : `Sent a file: ${file.name}`;

          await chatService.sendMessage(ticketChat.id, {
            text: textFallback,
            senderId: currentUser.uid,
            senderName: userData?.displayName || currentUser.email,
            type: isImage ? 'image' : 'file',
            fileUrl: url,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size
          });

          try {
            const recipients = (ticketChat.participants || []).filter((id) => id !== currentUser.uid);
            await Promise.all(
              recipients.map((recipientId) =>
                notificationService.createNotification({
                  recipientId,
                  type: 'chat_message',
                  title: ticketChat.title || ticket.title || 'New attachment',
                  body: textFallback,
                  resourceType: 'conversation',
                  resourceId: ticketChat.id,
                })
              )
            );
          } catch (e) {
            console.error('Failed to send attachment notifications:', e);
          }
        } catch (e) {
          console.error('Post-upload handling failed:', e);
        } finally {
          setUploading(false);
          event.target.value = '';
        }
      }
    );
  };

  const handleApproveBid = async (bidId) => {
    try {
      await bidService.approveBid(bidId, id);
      alert('Bid approved successfully! The service provider has been notified.');
    } catch (error) {
      console.error('Error approving bid:', error);
      alert('Error approving bid. Please try again.');
    }
  };

  const handleRejectBid = async (bidId) => {
    try {
      await bidService.rejectBid(bidId);
      alert('Bid rejected successfully!');
    } catch (error) {
      console.error('Error rejecting bid:', error);
      alert('Error rejecting bid. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'in-progress': return 'bg-slate-100 text-slate-800 border-slate-300';
      case 'completed': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'closed': return 'bg-slate-50 text-slate-500 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-600 mx-auto" />
          <p className="mt-4 text-sm font-medium text-slate-600">Loading ticket...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <ClipboardList className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
          </div>
          <h3 className="text-base font-semibold text-slate-800 mb-1">Ticket not found</h3>
          <p className="text-sm text-slate-600 mb-4">The ticket you're looking for doesn't exist.</p>
          <Link
            to="/tickets"
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            Back to Tickets
          </Link>
        </div>
      </div>
    );
  }

  const totalExpenses = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-3 min-w-0">
                <Link to="/tickets" className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors shrink-0">
                  <ArrowLeft className="w-5 h-5" strokeWidth={1.75} />
                </Link>
                <div className="min-w-0">
                  <h1 className="text-lg font-semibold text-slate-800 truncate">{ticket.title || 'Untitled Ticket'}</h1>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
                    {ticket.propertyName || 'Unknown Property'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getStatusColor(ticket.status)}`}>
                  {ticket.status}
                </span>
                {userData?.role === 'owner' && (
                  <select
                    value={ticket.status}
                    onChange={(e) => handleStatusUpdate(e.target.value)}
                    className="px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 text-slate-700"
                  >
                    <option value="open">Open</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="closed">Closed</option>
                  </select>
                )}
              </div>
            </div>
          </header>

          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                <h3 className="text-sm font-semibold text-slate-800">Details</h3>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Description</label>
                    <p className="text-sm text-slate-800 bg-slate-50 rounded-md p-2.5">
                      {ticket.description || 'No description provided'}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.75} />
                      <span className="text-slate-500">Created</span>
                      <span className="text-slate-800">{ticket.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.75} />
                      <span className="text-slate-500">Updated</span>
                      <span className="text-slate-800">{ticket.updatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                  Info
                </h3>
              </div>
              <div className="p-3 space-y-2">
                <div><label className="text-[10px] font-medium text-slate-500 uppercase">ID</label><p className="text-xs font-mono text-slate-800 truncate bg-slate-50 rounded px-1.5 py-0.5">{ticket.id}</p></div>
                <div><label className="text-[10px] font-medium text-slate-500 uppercase">Property</label><p className="text-xs text-slate-800">{ticket.propertyName || '—'}</p></div>
              </div>
            </div>

          <div className="space-y-4">
            {userData?.role === 'owner' && (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                    Providers
                  </h3>
                  {isOpenToBid && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-medium border border-slate-200">
                      <Check className="w-3 h-3" strokeWidth={1.75} /> Bidding open
                    </span>
                  )}
                </div>
                <div className="p-3">
                  {assignedProvidersLoading ? (
                    <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-200 border-t-slate-600" /></div>
                  ) : assignedProviders.length > 0 ? (
                    <div className="space-y-2">
                      {assignedProviders.map((p) => (
                        <div key={p.id} className="flex items-center gap-2 p-2 border border-slate-100 rounded-md">
                          <div className="w-7 h-7 rounded bg-slate-100 text-slate-700 flex items-center justify-center text-[10px] font-semibold shrink-0">
                            {(p.displayName || p.name || 'SP').slice(0,2).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium text-slate-800 truncate">{p.displayName || p.name || 'Provider'}</div>
                            <div className="text-[10px] text-slate-500 truncate">{p.company || p.email || ''}</div>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={() => { setShowReferralModal(true); generateReferralLink(); }}
                        className="w-full mt-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded text-xs font-medium flex items-center justify-center gap-1"
                      >
                        <Link2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Referral link
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-600">Open to bid or invite via referral.</p>
                      <button
                        onClick={() => { if (!isOpenToBid) handleOpenToBid(); }}
                        className={`w-full py-2 rounded text-xs font-medium ${isOpenToBid ? 'bg-slate-100 text-slate-600 border border-slate-200' : 'bg-slate-800 text-white hover:bg-slate-700'}`}
                      >
                        {isOpenToBid ? 'Bidding open' : 'Open to Bid'}
                      </button>
                      <button
                        onClick={() => { setShowReferralModal(true); generateReferralLink(); }}
                        className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs font-medium border border-slate-200"
                      >
                        Get referral link
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {userData?.role === 'owner' && isOpenToBid && (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">Bids</h3>
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-medium">
                    {bids.filter(b => b.status === 'pending').length} pending
                  </span>
                </div>
                <div className="p-3 max-h-64 overflow-y-auto">
                  {bidsLoading ? (
                    <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-200 border-t-slate-600" /></div>
                  ) : bids.length === 0 ? (
                    <p className="text-xs text-slate-500 py-3 text-center">No bids yet</p>
                  ) : (
                    <div className="space-y-2">
                      {bids.map((bid) => (
                        <div key={bid.id} className="border border-slate-200 rounded-md p-2.5">
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-6 h-6 rounded bg-slate-100 text-slate-700 flex items-center justify-center text-[10px] font-semibold shrink-0">
                                {(bid.providerName || 'SP').slice(0,2).toUpperCase()}
                              </div>
                              <span className="text-xs font-medium text-slate-800 truncate">{bid.providerName || 'Provider'}</span>
                            </div>
                            <span className="text-xs font-semibold text-slate-800 shrink-0">${bid.amount?.toFixed(2) || '0'}</span>
                          </div>
                          <p className="text-[10px] text-slate-500 line-clamp-2 mb-1.5">{bid.description || '—'}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-slate-400">{bid.submittedAt ? new Date(bid.submittedAt.toDate()).toLocaleDateString() : '—'}</span>
                            {bid.status === 'pending' && (
                              <div className="flex gap-1">
                                <button onClick={() => handleRejectBid(bid.id)} className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-[10px] font-medium hover:bg-slate-200">Reject</button>
                                <button onClick={() => handleApproveBid(bid.id)} className="px-2 py-1 bg-slate-800 text-white rounded text-[10px] font-medium hover:bg-slate-700">Approve</button>
                              </div>
                            )}
                            {bid.status === 'approved' && <span className="text-[10px] text-slate-600 font-medium">Approved</span>}
                            {bid.status === 'rejected' && <span className="text-[10px] text-slate-500">Rejected</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {userData?.role === 'owner' && (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                    Expenses
                  </h3>
                  <span className="text-xs font-semibold text-slate-800">${totalExpenses.toFixed(2)}</span>
                </div>
                <div className="p-3">
                  {expenses.length > 0 ? (
                    <ul className="space-y-1.5 mb-3 max-h-32 overflow-y-auto">
                      {expenses.map((exp) => (
                        <li key={exp.id} className="flex justify-between items-center py-1.5 px-2 rounded bg-slate-50 text-xs">
                          <span className="text-slate-800 truncate flex-1 mr-2">{exp.description}</span>
                          <span className="font-medium text-slate-800 shrink-0">${exp.amount?.toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-500 mb-3">No expenses yet</p>
                  )}
                  <form onSubmit={handleAddExpense} className="border-t border-slate-200 pt-3 space-y-2">
                    <input type="number" step="0.01" placeholder="Amount" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })} className="w-full px-2.5 py-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-slate-400" required />
                    <input type="text" placeholder="Category" value={newExpense.category} onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })} className="w-full px-2.5 py-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-slate-400" required />
                    <input type="text" placeholder="Description" value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} className="w-full px-2.5 py-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-slate-400" required />
                    <button type="submit" className="w-full py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded text-xs font-medium">Add</button>
                  </form>
                </div>
              </div>
            )}

          </div>
          </div>

          <div className="lg:col-span-1 flex flex-col min-h-0 lg:border-l lg:border-slate-200 lg:pl-5">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden flex flex-col flex-1 min-h-0">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                  Messages
                </h3>
                {userData?.role === 'owner' && (
                  <button
                    onClick={() => { if (!isOpenToBid) handleOpenToBid(); }}
                    className={`px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                      isOpenToBid ? 'bg-slate-100 text-slate-600 border border-slate-200' : 'bg-slate-800 text-white hover:bg-slate-700'
                    }`}
                  >
                    {isOpenToBid ? 'Bidding open' : 'Open to Bid'}
                  </button>
                )}
              </div>
              <div className="p-3 flex flex-col flex-1 min-h-0">
                <div className="space-y-2 mb-3 flex-1 min-h-0 overflow-y-auto">
                  {chatLoading ? (
                    <div className="flex items-center justify-center h-full min-h-[12rem]">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-slate-600" />
                    </div>
                  ) : chatMessages.length === 0 ? (
                    <div className="text-center py-6">
                      <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
                      <p className="text-xs text-slate-500">No messages yet</p>
                    </div>
                  ) : (
                    chatMessages.map((message) => (
                      <div key={message.id} className={`flex ${message.senderId === currentUser.uid ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] px-3 py-1.5 rounded-lg text-sm ${
                          message.senderId === currentUser.uid ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-800'
                        }`}>
                          {message.senderId !== currentUser.uid && (
                            <div className="text-[10px] font-medium text-slate-500 mb-0.5">{message.senderName}</div>
                          )}
                          {message.type === 'image' && message.fileUrl ? (
                            <a href={message.fileUrl} target="_blank" rel="noopener noreferrer">
                              <img src={message.fileUrl} alt="" className="rounded max-h-40" />
                            </a>
                          ) : message.type === 'file' && message.fileUrl ? (
                            <a href={message.fileUrl} target="_blank" rel="noopener noreferrer" className="underline">{message.fileName || 'File'}</a>
                          ) : (
                            message.text
                          )}
                          <div className={`text-[10px] mt-0.5 ${message.senderId === currentUser.uid ? 'text-slate-300' : 'text-slate-500'}`}>
                            {message.createdAt?.toDate?.()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || ''}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <form onSubmit={handleSendChatMessage} className="flex gap-2 border-t border-slate-200 pt-3 shrink-0">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 min-w-0 px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
                  />
                  <label className="inline-flex items-center px-2.5 py-2 border border-slate-200 rounded text-slate-600 hover:bg-slate-50 cursor-pointer text-xs font-medium shrink-0">
                    <Paperclip className="w-4 h-4" strokeWidth={1.75} />
                    <input type="file" className="hidden" onChange={handleFileSelected} />
                  </label>
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || !ticketChat || sending}
                    className="px-3 py-2 bg-slate-800 text-white rounded text-sm font-medium hover:bg-slate-700 disabled:opacity-50 shrink-0 inline-flex items-center gap-1"
                  >
                    <Send className="w-4 h-4" strokeWidth={1.75} />
                    {sending ? '…' : 'Send'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

      {showReferralModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full border border-slate-200">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                  Referral link
                </h2>
                <button onClick={() => setShowReferralModal(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded">
                  <X className="w-5 h-5" strokeWidth={1.75} />
                </button>
              </div>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded p-2.5">
                  <p className="text-[10px] font-medium text-slate-500 uppercase mb-1">Your link</p>
                  <div className="flex gap-2">
                    <input type="text" value={referralLink} readOnly className="flex-1 min-w-0 px-2 py-1.5 border border-slate-200 rounded text-xs bg-white text-slate-800" />
                    <button onClick={copyToClipboard} className="px-2.5 py-1.5 bg-slate-800 text-white rounded text-xs font-medium hover:bg-slate-700 shrink-0">Copy</button>
                  </div>
                </div>
                <p className="text-xs text-slate-600">Share with providers. You earn $50 when they join.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddProviderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full border border-slate-200">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                  Add provider
                </h3>
                <button onClick={() => setShowAddProviderModal(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded">
                  <X className="w-5 h-5" strokeWidth={1.75} />
                </button>
              </div>
              <p className="text-xs text-slate-600 mb-4">Use the referral link to invite providers you know.</p>
              <button type="button" onClick={() => setShowAddProviderModal(false)} className="w-full py-2 border border-slate-200 text-slate-700 rounded text-xs font-medium hover:bg-slate-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail; 